/**
 * `/settings/checklists` — checklist templates (ADMIN).
 *
 * Same shape as the other Step 2 settings screens, and the same rule: no
 * delete, only deactivation. A template that stops being offered leaves every
 * checklist already made from it working, because a checklist is a COPY — the
 * note on this page says so, because it is the thing an administrator will
 * most reasonably assume otherwise.
 */
import { useMutation, useQuery } from '@apollo/client';
import { Button } from '@meshhub/design-tokens/ui';
import { ListChecks, Plus } from 'lucide-react';
import React, { useState } from 'react';
import {
  FormDialog,
  LoadingOrEmpty,
  MutationError,
  TextArea,
  TextField,
} from '../../components/FormKit.web.jsx';
import { formatCount } from '../../lib/format.js';
import {
  CHECKLIST_TEMPLATES_QUERY,
  CREATE_CHECKLIST_TEMPLATE,
  SET_CHECKLIST_TEMPLATE_ACTIVE,
  UPDATE_CHECKLIST_TEMPLATE,
} from '../../lib/queries.js';
import { S } from '../../lib/strings.js';

const emptyDraft = { title: '', description: '', items: '' };

export function ChecklistsScreen() {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);

  const { data, loading, error, refetch } = useQuery(CHECKLIST_TEMPLATES_QUERY, {
    variables: { includeInactive: true },
  });
  const [createTemplate, createState] = useMutation(CREATE_CHECKLIST_TEMPLATE, {
    onCompleted: () => refetch(),
  });
  const [updateTemplate, updateState] = useMutation(UPDATE_CHECKLIST_TEMPLATE, {
    onCompleted: () => refetch(),
  });
  const [setActive, activeState] = useMutation(SET_CHECKLIST_TEMPLATE_ACTIVE, {
    onCompleted: () => refetch(),
  });

  const templates = data?.checklistTemplates ?? [];

  function open(template) {
    setEditing(template ?? { id: null });
    setDraft(
      template
        ? {
            title: template.title,
            description: template.description ?? '',
            // One item per line: the fastest way to write six steps, and the
            // fastest way to read them back.
            items: template.items.map((item) => item.title).join('\n'),
          }
        : emptyDraft,
    );
  }

  async function save() {
    const items = draft.items
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    const input = { title: draft.title, description: draft.description || null, items };
    if (editing?.id) {
      await updateTemplate({ variables: { id: editing.id, input } });
    } else {
      await createTemplate({ variables: { input } });
    }
    setEditing(null);
  }

  const itemCount = draft.items.split('\n').filter((line) => line.trim()).length;

  return (
    <div className="flex flex-col gap-4 p-5">
      <header className="flex items-center gap-3">
        <ListChecks size={18} className="text-primary" aria-hidden="true" />
        <h1 className="text-md font-bold">{S.checklists.templates}</h1>
        <Button type="button" variant="primary" className="ms-auto" onClick={() => open(null)}>
          <Plus size={15} aria-hidden="true" />
          {S.checklists.newTemplate}
        </Button>
      </header>

      <p className="text-sm text-text-muted">{S.checklists.copyNote}</p>

      <MutationError
        error={error ?? createState.error ?? updateState.error ?? activeState.error}
      />

      <LoadingOrEmpty
        loading={loading}
        empty={templates.length === 0}
        emptyTitle={S.checklists.empty}
      />

      <ul className="flex flex-col gap-2">
        {templates.map((template) => (
          <li
            key={template.id}
            className={[
              'flex items-center gap-4 rounded border border-border bg-surface px-4 py-3',
              template.isActive ? '' : 'opacity-60',
            ].join(' ')}
          >
            <div className="flex flex-1 flex-col">
              <span className="font-medium">{template.title}</span>
              <span className="text-xs text-text-muted">
                {formatCount(template.items.length)} {S.checklists.items}
                {template.description ? ` · ${template.description}` : ''}
              </span>
            </div>

            {template.isSystem && (
              <span className="rounded bg-surface-hover px-2 py-px text-xs text-text-muted">
                {S.app.system}
              </span>
            )}

            <Button type="button" onClick={() => open(template)}>
              {S.app.edit}
            </Button>
            <Button
              type="button"
              onClick={() =>
                setActive({
                  variables: { id: template.id, isActive: !template.isActive },
                })
              }
            >
              {template.isActive ? S.app.deactivate : S.app.activate}
            </Button>
          </li>
        ))}
      </ul>

      <FormDialog
        open={Boolean(editing)}
        onOpenChange={(open_) => !open_ && setEditing(null)}
        title={editing?.id ? S.checklists.editTemplate : S.checklists.newTemplate}
        submitLabel={editing?.id ? S.app.save : S.app.create}
        busy={createState.loading || updateState.loading}
        onSubmit={save}
      >
        <TextField
          label={S.checklists.templateTitle}
          value={draft.title}
          onChange={(event) => setDraft((d) => ({ ...d, title: event.target.value }))}
          required
        />
        <TextField
          label={S.checklists.templateDescription}
          value={draft.description}
          onChange={(event) => setDraft((d) => ({ ...d, description: event.target.value }))}
        />
        <TextArea
          label={S.checklists.items}
          hint={itemCount === 0 ? S.checklists.itemsRequired : S.checklists.itemsHint}
          error={itemCount === 0 ? S.checklists.itemsRequired : undefined}
          rows={8}
          value={draft.items}
          onChange={(event) => setDraft((d) => ({ ...d, items: event.target.value }))}
        />
      </FormDialog>
    </div>
  );
}
